import { isEqual } from "lodash";
import mapboxgl, { CirclePaint } from "mapbox-gl";

import { Env } from "@/shared/environment";
import { MapPath } from "@/shared/state/visualization/schema/v1";
import {
  NS_PER_S,
  nanoSecToLocalTimestamp,
  nanoSecToSecLossy,
} from "@/shared/time";

import { ListenerType, Timer } from "../../timer/Timer";

import { defaultActiveMarker, defaultMarker } from "./colors";
import { cameraFitForPathBounds, cameraFitForVisiblePaths } from "./geoBounds";
import { TimestampedLocation } from "./geoTypes";
import { MapDataManager, MapLoadingState } from "./MapDataManager";
import { MapPathData } from "./MapPathData";
import { StyleToggleControl } from "./StyleToggleControl";

const activeLayerStyle: CirclePaint = {
  "circle-radius": 6,
  "circle-color": defaultActiveMarker,
};

/**
 * Window-level events mapbox forwards into Map.resize(): browser window
 * resize, device rotation, and entering or leaving fullscreen. Map.resize()
 * fires movestart carrying that event as originalEvent even though the camera
 * has not moved, so these must not count as the user taking the camera over.
 */
const resizeEventTypes = new Set([
  "resize",
  "orientationchange",
  "fullscreenchange",
  "webkitfullscreenchange",
]);

/**
 * Responsible for rendering the map with data.
 * Internally uses Mapbox GL to render the map
 */
export class MapRenderer {
  #abortController: AbortController;
  #map: mapboxgl.Map | null = null;
  #dataManager: MapDataManager;
  #timer?: Timer;
  #state?: MapPath[];
  #mapContainer: HTMLDivElement;
  #shouldFitCameraToPathBounds: boolean;
  #setSingleActiveLocation: (arg: TimestampedLocation | null) => void;
  #dataLoadHold: symbol | null = null;
  #styleLoadHold: symbol | null = null;
  #popup: mapboxgl.Popup | null = null;
  #onViewportChange?: (viewport: {
    center: { lng: number; lat: number };
    zoom: number;
    bearing: number;
    pitch: number;
  }) => void;
  #initialViewport?: {
    center: { lng: number; lat: number };
    zoom: number;
    bearing?: number;
    pitch?: number;
  };

  constructor({
    abortController,
    mapContainer,
    onError,
    setLoadingState,
    timer,
    setSingleActiveLocation,
    onViewportChange,
    initialViewport,
  }: {
    abortController: AbortController;
    mapContainer: HTMLDivElement;
    onError: (error: Error) => void;
    setLoadingState: (state: MapLoadingState) => void;
    timer: Timer;
    setSingleActiveLocation: (arg: TimestampedLocation | null) => void;
    onViewportChange?: (viewport: {
      center: { lng: number; lat: number };
      zoom: number;
      bearing: number;
      pitch: number;
    }) => void;
    initialViewport?: {
      center: { lng: number; lat: number };
      zoom: number;
      bearing?: number;
      pitch?: number;
    };
  }) {
    this.#abortController = abortController;
    this.#mapContainer = mapContainer;
    this.#timer = timer;
    this.#setSingleActiveLocation = setSingleActiveLocation;
    this.#onViewportChange = onViewportChange;
    this.#initialViewport = initialViewport;
    // A viewport restored from a shared workspace or a saved layout is the
    // user's own framing, so it wins over fitting the camera to path bounds
    this.#shouldFitCameraToPathBounds = initialViewport === undefined;

    // Create and wire up the data manager
    this.#dataManager = new MapDataManager({ onError });
    this.#dataManager.setOnPathUpdated(this.#onPathDataUpdated);
    this.#dataManager.setOnLoadingStateChange((state) => {
      setLoadingState(state);

      if (state !== "complete") {
        this.#dataLoadHold ??= this.#timer?.hold() ?? null;
      } else {
        if (this.#dataLoadHold) {
          this.#timer?.holdRelease(this.#dataLoadHold);
          this.#dataLoadHold = null;
        }
        // Now that all data is available, render the active location markers
        this.drawActiveLocation();
      }
    });
  }

  public initialize(): Promise<[void, boolean]> {
    return Promise.all([
      this.#initializeMap(),
      this.#dataManager.waitForInitialized(),
    ]);
  }

  #initializeMap(): Promise<void> {
    return new Promise((resolve, reject) => {
      mapboxgl.accessToken = Env.robotoMapboxToken;

      // Use initial viewport if provided, otherwise use defaults
      const center: [number, number] = this.#initialViewport?.center
        ? [this.#initialViewport.center.lng, this.#initialViewport.center.lat]
        : [0, 0];
      const zoom = this.#initialViewport?.zoom ?? 19;
      const bearing = this.#initialViewport?.bearing ?? 0;
      const pitch = this.#initialViewport?.pitch ?? 0;

      this.#map = new mapboxgl.Map({
        container: this.#mapContainer,
        center,
        style: "mapbox://styles/mapbox/outdoors-v12", // https://docs.mapbox.com/api/maps/styles/
        zoom: zoom,
        bearing: bearing,
        pitch: pitch,
      });

      this.#map.on("error", (evt) => {
        reject(evt.error);
      });

      // Once the user moves the camera themselves, they own it: stop fitting
      // to path bounds for the rest of the panel's life. Mapbox sets
      // originalEvent on movestart for camera gestures (drag, wheel,
      // keyboard, double-click, NavigationControl) and leaves it unset for
      // programmatic moves such as fitBounds, so its presence is the signal.
      this.#map.on("movestart", (e) => {
        if (e.originalEvent && !resizeEventTypes.has(e.originalEvent.type)) {
          this.#shouldFitCameraToPathBounds = false;
        }
      });
      // Box zoom (shift+drag) moves the camera through fitScreenCoordinates,
      // a programmatic call, so its movestart carries no originalEvent —
      // but boxzoomend does
      this.#map.on("boxzoomend", () => {
        this.#shouldFitCameraToPathBounds = false;
      });
      // Scroll zoom is the other gesture movestart cannot see. Its handler
      // changes the camera from a render frame rather than from the wheel
      // event, and that path fires movestart with no event data at all.
      // Measured against mapbox-gl 3.4.0: a wheel gesture that demonstrably
      // moves the camera reports originalEvent as undefined, while dblclick
      // and keydown both carry theirs. So watch the gesture itself. A wheel
      // over the map is the user reaching for the camera whether or not
      // mapbox acts on it, and Map.resize() cannot manufacture one.
      this.#map.getCanvasContainer().addEventListener(
        "wheel",
        () => {
          this.#shouldFitCameraToPathBounds = false;
        },
        { signal: this.#abortController.signal },
      );

      // Listen for viewport changes
      this.#map.on("moveend", () => {
        if (!this.#map || !this.#onViewportChange) {
          return;
        }

        const center = this.#map.getCenter();
        const zoom = this.#map.getZoom();
        const bearing = this.#map.getBearing();
        const pitch = this.#map.getPitch();

        this.#onViewportChange({
          center: { lng: center.lng, lat: center.lat },
          zoom,
          bearing,
          pitch,
        });
      });

      this.#map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: false,
        }),
        "top-right",
      );

      // Enables user to open map in fullscreen
      this.#map.addControl(new mapboxgl.FullscreenControl(), "top-right");

      // Enables user to toggle between map tiles (style)
      this.#map.addControl(
        new StyleToggleControl({
          onStyleLoad: () => {
            // Mapbox destroys all sources/layers on style switch.
            // Re-draw data + active location for every path that has data.
            for (const path of this.#state ?? []) {
              const pathData = this.#dataManager.getPathData(path.id);
              if (pathData && pathData.length > 0) {
                this.drawPathData(path.id);
              }
            }
            this.drawActiveLocation();
          },
        }),
        "top-right",
      );

      this.#map.on("click", this.onMapClick.bind(this));

      const timerEvents: ListenerType[] = ["tick", "seek", "start", "stop"];
      timerEvents.forEach((event) =>
        this.#timer?.addListener(event, this.drawActiveLocation.bind(this), {
          signal: this.#abortController.signal,
        }),
      );
      this.#map.on("styledataloading", () => {
        if (this.#styleLoadHold !== null || this.#timer === undefined) {
          return;
        }
        this.#styleLoadHold = this.#timer?.hold();
      });
      this.#map.on("styledata", () => {
        if (this.#styleLoadHold === null || this.#timer === undefined) {
          return;
        }
        this.#timer?.holdRelease(this.#styleLoadHold);
        this.#styleLoadHold = null;
      });

      this.#popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: "300px",
        offset: 10,
        anchor: "top",
      });

      this.#map.once("style.load", resolve);
    });
  }

  /**
   * Fit the camera to one path's full extent. Invoked when the user picks a
   * path in the legend; that pick is the user choosing the framing, so later
   * data chunks stop re-fitting the camera to every visible path. Does
   * nothing until the path has coordinates.
   */
  public fitCameraToPath(pathId: string): void {
    if (!this.#map) {
      return;
    }

    const cameraFit = cameraFitForPathBounds(
      this.#dataManager.getPathData(pathId)?.getBounds(),
    );
    if (!cameraFit) {
      return;
    }

    this.#shouldFitCameraToPathBounds = false;
    this.#map.fitBounds(cameraFit.corners, cameraFit.options);
  }

  /**
   * Delete the map and all associated layers/sources
   */
  public dispose() {
    // Abort any ongoing operations
    this.#abortController.abort();

    // Dispose of the data manager
    this.#dataManager.dispose();

    // Dispose of the map
    this.#map?.remove();

    // Dispose of the popup
    this.#popup?.remove();

    // Clear stored references
    this.#map = null;
    this.#popup = null;
    this.#timer = undefined;
    this.#state = undefined;
  }

  /**
   * Render the map again in the available container
   */
  public resize(): void {
    if (!this.#map) {
      return;
    }

    this.#map.resize();
  }

  /**
   * Update the map state given changes to the state
   */
  public setState(state: MapPath[], abortSignal?: AbortSignal): void {
    if (!this.#map) {
      return;
    }

    // 1. Remove old paths — tear down Mapbox sources/layers and tell dataManager to dispose
    this.removeOldPaths(state);

    // 2. Reload paths whose coordinateUnits have changed
    this.reloadPathsWithChangedUnits(state, abortSignal);

    // 3. Initiate data fetches — for each path that doesn't have MapPathData, call loadPathData
    this.addNewPaths(state, abortSignal);

    // 4. Update internal state
    this.#state = state;

    for (const path of state) {
      if (this.#map.getSource(path.id)) {
        this.drawPathStyle(path);
      }
    }

    // 5. Re-frame the camera. Paths may have been added, removed, or toggled
    // hidden since the last call, and for paths that finished loading before
    // that change there is no later data chunk to trigger the fit.
    this.fitCameraToVisiblePathBounds();
  }

  private addNewPaths(state: MapPath[], abortSignal?: AbortSignal): void {
    for (const path of state) {
      if (this.#state?.some((existing) => existing.id === path.id)) {
        continue;
      }

      // Load the path data if it doesn't exist yet
      if (!this.#dataManager.getPathData(path.id)) {
        this.#dataManager.loadPathData(path, abortSignal);
      }
    }
  }

  /**
   * Reload paths whose coordinateUnits have changed (dispose and reload).
   */
  private reloadPathsWithChangedUnits(
    state: MapPath[],
    abortSignal?: AbortSignal,
  ): void {
    for (const newPath of state) {
      const existingPath = this.#state?.find((p) => p.id === newPath.id);
      if (!existingPath) {
        continue;
      }

      if (isEqual(existingPath.coordinateUnits, newPath.coordinateUnits)) {
        continue;
      }

      const circleLayerId = this.getCircleLayerId(newPath.id);
      const lineLayerId = this.getLineLayerId(newPath.id);
      const activeLayerId = this.getActiveLayerId(newPath.id);

      if (this.#map?.getLayer(circleLayerId)) {
        this.#map.removeLayer(circleLayerId);
      }
      if (this.#map?.getLayer(lineLayerId)) {
        this.#map.removeLayer(lineLayerId);
      }

      if (this.#map?.getLayer(activeLayerId)) {
        this.#map.removeLayer(activeLayerId);
      }

      this.#map?.removeSource(newPath.id);

      if (this.#map?.getSource(activeLayerId)) {
        this.#map.removeSource(activeLayerId);
      }

      this.#dataManager.disposePath(existingPath);
      this.#dataManager.loadPathData(newPath, abortSignal);
    }
  }

  /**
   * Safely add source and layer(s) to map once style has loaded
   */
  private addSourceAndLayers(
    sourceId: string,
    sourceData: mapboxgl.AnySourceData,
    layers: mapboxgl.AnyLayer[],
  ) {
    if (!this.#map) {
      return;
    }

    // Add the source
    if (!this.#map?.getSource(sourceId)) {
      this.#map?.addSource(sourceId, sourceData);
    }

    // Add the layers
    for (const layer of layers) {
      if (!this.#map?.getLayer(layer.id)) {
        this.#map?.addLayer(layer);
      }
    }
  }

  /**
   * Create a GeoJSON symbol layer for active markers.
   * This is used to highlight the nearest marker given the current time.
   */
  private createActiveLocationGeoJSON(
    location: TimestampedLocation,
    pathId: string,
  ): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {
            id: pathId,
            timestamp: location.timestamp,
          },
          geometry: {
            type: "Point",
            coordinates: [location.longitude, location.latitude],
          },
        },
      ],
    } as GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  }

  /**
   * Convert MapPathData to GeoJSON
   * This enables markers to be displayed in a performant symbol layer rendered by WebGL.
   */
  private createLocationsGeoJSON(
    pathData: MapPathData,
  ): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    const points: GeoJSON.Feature<GeoJSON.Geometry>[] = [];

    for (let i = 0; i < pathData.length; i++) {
      const coord = pathData.getCoordinate(i);
      if (!coord) {
        continue;
      }

      points.push({
        type: "Feature",
        properties: {
          id: i,
          timestamp: nanoSecToSecLossy(coord.logTime),
        },
        geometry: {
          type: "Point",
          coordinates: [coord.lon, coord.lat],
        },
      });
    }

    const lineCoordinates = pathData.toGeoJSONCoordinates();

    return {
      type: "FeatureCollection",
      features: [
        ...points,
        {
          type: "Feature",
          properties: {
            id: "line",
          },
          geometry: {
            type: "LineString",
            coordinates: lineCoordinates,
          },
        },
      ],
    } as GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  }

  /**
   * Create/update Mapbox source + layers with GeoJSON
   * Called from #onPathDataUpdated on every data chunk, and from
   * StyleToggleControl's onStyleLoad after a tile-style switch
   */
  private drawPathData(pathId: string): void {
    if (!this.#map) {
      return;
    }

    const pathData = this.#dataManager.getPathData(pathId);
    if (!pathData || pathData.length === 0) {
      return;
    }

    const path = this.#state?.find((p) => p.id === pathId);
    if (!path) {
      return;
    }

    const featureCollection = this.createLocationsGeoJSON(pathData);

    // If source already exists, update it and exit
    const existingSource = this.#map.getSource(pathId);
    if (existingSource && existingSource.type === "geojson") {
      // Updating the source data will automatically update the layers
      existingSource.setData(featureCollection);
      return;
    }

    this.addSourceAndLayers(
      pathId,
      { type: "geojson", data: featureCollection },
      [
        {
          id: this.getCircleLayerId(pathId),
          type: "circle",
          source: pathId,
          layout: {
            visibility: path.visible ? "visible" : "none",
          },
          paint: {
            "circle-radius": 3,
            "circle-color":
              (path.style["lineColor"] as string) || defaultMarker,
          },
        },
        {
          id: this.getLineLayerId(pathId),
          type: "line",
          source: pathId,
          layout: {
            "line-join": "round",
            "line-cap": "round",
            visibility: path.visible ? "visible" : "none",
          },
          paint: {
            "line-color": (path.style["lineColor"] as string) || defaultMarker,
            "line-width": 1,
          },
        },
      ],
    );
    this.setupHoverInteraction(this.getCircleLayerId(pathId));
  }

  /**
   * Update paint/layout properties only (no GeoJSON rebuild)
   * Called from setState for already-rendered paths
   */
  private drawPathStyle(path: MapPath): void {
    if (!this.#map) {
      return;
    }

    const circleLayerId = this.getCircleLayerId(path.id);
    const lineLayerId = this.getLineLayerId(path.id);
    const activeLayerId = this.getActiveLayerId(path.id);

    // Update colors
    if (this.#map.getLayer(circleLayerId) && path.style["lineColor"]) {
      this.#map.setPaintProperty(
        circleLayerId,
        "circle-color",
        path.style["lineColor"],
      );
    }
    if (this.#map.getLayer(lineLayerId)) {
      this.#map.setPaintProperty(
        lineLayerId,
        "line-color",
        path.style["lineColor"],
      );
    }

    // Update visibility
    const visibility = path.visible ? "visible" : "none";
    if (this.#map.getLayer(circleLayerId)) {
      this.#map.setLayoutProperty(circleLayerId, "visibility", visibility);
    }
    if (this.#map.getLayer(lineLayerId)) {
      this.#map.setLayoutProperty(lineLayerId, "visibility", visibility);
    }
    if (this.#map.getLayer(activeLayerId)) {
      this.#map.setLayoutProperty(activeLayerId, "visibility", visibility);
    }
  }

  /**
   * Update active marker source for each path
   * Called from timer tick/seek/start/stop; StyleToggleControl onStyleLoad
   */
  private drawActiveLocation(): void {
    if (!this.#map || !this.#state || !this.#timer) {
      return;
    }

    for (const path of this.#state) {
      const pathData = this.#dataManager.getPathData(path.id);
      if (!pathData || pathData.length === 0) {
        continue;
      }

      // Binary-search for the nearest index
      const newIndex = pathData.findNearestIndex(this.#timer.currentTime);
      const coord = pathData.getCoordinate(newIndex);

      if (!coord) {
        continue;
      }

      const timestampedLocation = {
        latitude: coord.lat,
        longitude: coord.lon,
        timestamp: nanoSecToSecLossy(coord.logTime),
      };

      // If there's exactly one path, call #setSingleActiveLocation
      if (this.#state.length === 1) {
        this.#setSingleActiveLocation(timestampedLocation);
      }

      // Build single-point GeoJSON and update/create the active source + layer
      const activeLocationsGeoJSON = this.createActiveLocationGeoJSON(
        timestampedLocation,
        path.id,
      );

      const activeSourceId = this.getActiveLayerId(path.id);
      const activeSource = this.#map.getSource(activeSourceId);
      if (activeSource && activeSource.type === "geojson") {
        activeSource.setData(activeLocationsGeoJSON);
      } else {
        this.addSourceAndLayers(
          activeSourceId,
          { type: "geojson", data: activeLocationsGeoJSON },
          [
            {
              id: activeSourceId,
              type: "circle",
              layout: {
                visibility: path.visible ? "visible" : "none",
              },
              source: activeSourceId,
              paint: activeLayerStyle,
            },
          ],
        );
      }
    }
  }

  /**
   * Fit the camera to the merged bounds of every visible path, so the whole
   * panel's data is in view. Runs after each streamed data chunk, unless the
   * panel opened with a restored viewport or the user has taken the camera
   * over since (a camera gesture, or picking a path in the legend).
   */
  private fitCameraToVisiblePathBounds(): void {
    if (!this.#map || !this.#shouldFitCameraToPathBounds) {
      return;
    }

    const cameraFit = cameraFitForVisiblePaths(this.#state ?? [], (pathId) =>
      this.#dataManager.getPathData(pathId)?.getBounds(),
    );
    if (cameraFit) {
      this.#map.fitBounds(cameraFit.corners, cameraFit.options);
    }
  }

  /**
   * Get name for an active layer given pathId
   */
  private getActiveLayerId(pathId: string) {
    return `${pathId}-active`;
  }

  /**
   * Get name for a circle layer given pathId
   */
  private getCircleLayerId(pathId: string) {
    return `${pathId}-circle`;
  }

  /**
   * Get name for a line layer given pathId
   */
  private getLineLayerId(pathId: string) {
    return `${pathId}-line`;
  }

  /**
   * Callback fired when a chunk of path data is received
   */
  #onPathDataUpdated = (pathId: string): void => {
    this.drawPathData(pathId);
    this.fitCameraToVisiblePathBounds();
  };

  /**
   * Set global timer to the time associated with any clicked map points
   */
  private onMapClick(event: mapboxgl.MapLayerMouseEvent): void {
    if (!this.#map) {
      return;
    }

    // Query all features at the clicked point
    const features = this.#map.queryRenderedFeatures(event.point);

    if (features.length) {
      // Find the first Point feature with a timestamp property
      const clickedFeature = features.find(
        (feature) =>
          feature.geometry.type === "Point" &&
          feature.properties?.timestamp !== undefined,
      );

      if (clickedFeature && clickedFeature.properties) {
        const timestamp = clickedFeature.properties.timestamp as number;

        // Ensure the timestamp is valid
        if (!isNaN(timestamp)) {
          // Seek to the corresponding time in the timer
          this.#timer?.seekTo(BigInt(Math.round(timestamp * NS_PER_S)));
        }
      }
    }
  }

  /**
   * Remove old paths that are not present in the new state
   */
  private removeOldPaths(state: MapPath[]): void {
    const pathsToRemove = (this.#state ?? []).filter((existingPaths) => {
      const matching = state.find((path) => path.id === existingPaths.id);
      return matching === undefined;
    });

    for (const path of pathsToRemove) {
      const circleLayerId = this.getCircleLayerId(path.id);
      const lineLayerId = this.getLineLayerId(path.id);
      const activeLayerId = this.getActiveLayerId(path.id);

      if (this.#map?.getLayer(circleLayerId)) {
        this.#map.removeLayer(circleLayerId);
      }
      if (this.#map?.getLayer(lineLayerId)) {
        this.#map.removeLayer(lineLayerId);
      }

      /**
       * The active layer is only added when its visible, so we need
       * to check if it exists before trying to remove it
       */
      if (this.#map?.getLayer(activeLayerId)) {
        this.#map.removeLayer(activeLayerId);
      }

      // Remove sources after their layers are gone
      this.#map?.removeSource(path.id);

      /**
       * The active layer is only added when its visible, so we need
       * to check if it exists before trying to remove it
       */
      if (this.#map?.getSource(activeLayerId)) {
        this.#map.removeSource(activeLayerId);
      }

      // Dispose the path in the data manager
      this.#dataManager.disposePath(path);
    }
  }

  /**
   * Set up hover interaction for a specific path on the map
   */
  private setupHoverInteraction(pathId: string): void {
    // Show popup on hover
    this.#map?.on("mousemove", pathId, (e) => {
      if (!this.#map) {
        return;
      }

      this.#map.getCanvas().style.cursor = "pointer";

      if (e.features && e.features.length) {
        // Find the first Point feature with a valid timestamp property
        const hoveredFeature = e.features.find(
          (feature) =>
            feature.geometry.type === "Point" &&
            feature.properties?.timestamp !== undefined,
        );

        if (hoveredFeature && hoveredFeature.properties) {
          const coordinates = e.lngLat;
          const timestamp = hoveredFeature.properties.timestamp as number;

          // Ensure the timestamp is valid
          if (isNaN(timestamp)) {
            return;
          }

          // Ensure the popup is anchored to the point and displays correctly
          this.#popup
            ?.setLngLat(coordinates)
            .setHTML(
              `
            <div style="padding-top: 2px;color: #333333;">
              <div>${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}</div>
              <div>${nanoSecToLocalTimestamp(BigInt(Math.round(timestamp * NS_PER_S)))}</div>
            </div>
          `,
            )
            .addTo(this.#map);
        }
      }
    });

    // Hide popup when not hovering
    this.#map?.on("mouseleave", pathId, () => {
      if (!this.#map) {
        return;
      }
      this.#map.getCanvas().style.cursor = "";
      this.#popup?.remove();
    });
  }
}

// TODO:
// De-dupe issue Gabe mentioned on PR
// Determine strategy for handling active point in multi-series case
